import { callGateway, type CallerIdentity } from "./gateway";
import { ApiError } from "./errors";
import { config } from "../config";

/**
 * The plan/catalogue client.
 *
 * 🚨 A PLAN IS NOT ONE BACKEND. What an operator calls "a plan" is a row in
 * radcatalog (the group: name, heading, description, status) PLUS a set of
 * RADIUS attribute rows in radpolicy (the check/reply/label rows that are the
 * actual enforcement). Creating only the first produces a plan that AUTHORISES
 * SUBSCRIBERS WITH NO LIMITS -- no quota, no expiry, no bandwidth ceiling.
 *
 * Measured 2026-08-30: radcatalog's AddRadgroup writes the group only, and its
 * radpolicyclient.go calls radpolicy for exactly one thing -- cascadeDelete.
 * There is no create cascade. The composition is the CALLER's job, and this
 * file is that caller.
 */

/** radcatalog `model.RadGroup` -- app/model/radius.go:293, field for field. */
export interface RadGroup {
  id?: string;
  namespace: string;
  groupname: string;
  groupuid?: string;
  group_hd: string;
  group_desc: string;
  status: boolean;
}

/**
 * radpolicy `Radgroupcheck` / `Radgroupreply` / `RadgroupLabel` --
 * app/model/radius.go:20,43,31. The three share a shape; only `plock` (label
 * and annotation) differs, and the server defaults it.
 */
export interface RadgroupAttribute {
  id?: string;
  namespace: string;
  groupname: string;
  groupuid?: string;
  attribute: string;
  op: string;
  value: string;
}

/** Which radaccess proxy prefix each backend sits behind. */
const RADGROUP_PREFIX = "/radgroup";
const RADPOLICY_PREFIX = "/radpolicy";

export function listPlans(identity: CallerIdentity, namespace: string) {
  return callGateway<RadGroup[]>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/all/${encodeURIComponent(namespace)}`,
  );
}

/**
 * Close a plan to new sales (owner 2026-10-11, design §9.16): radcatalog
 * iStatus -1. Subscribers already on it keep renewing at its old price; the
 * shop stops offering it. Nothing is deleted.
 */
export function closePlan(identity: CallerIdentity, namespace: string, groupname: string) {
  return callGateway<RadGroup>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/deactivate/${encodeURIComponent(namespace)}/${encodeURIComponent(groupname)}`,
    { method: "PUT" },
  );
}

/** Put a closed plan back on sale: radcatalog iStatus -1 -> 0 (radcatalog v0.1.32). */
export function reopenPlan(identity: CallerIdentity, namespace: string, groupname: string) {
  return callGateway<RadGroup>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/reopen/${encodeURIComponent(namespace)}/${encodeURIComponent(groupname)}`,
    { method: "PUT" },
  );
}

/**
 * Rename a plan or change its description (owner 2026-10-15). radcatalog
 * writes group_hd, group_desc and status only -- never a limit or a price.
 */
export function updatePlanText(identity: CallerIdentity, group: RadGroup, heading: string, description: string) {
  return callGateway<RadGroup>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/${encodeURIComponent(group.namespace)}/${encodeURIComponent(group.groupname)}`,
    {
      method: "PUT",
      body: { namespace: group.namespace, groupname: group.groupname, group_hd: heading, group_desc: description, status: group.status },
    },
  );
}

export function createRadgroup(identity: CallerIdentity, group: RadGroup) {
  return callGateway<RadGroup>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/${encodeURIComponent(group.namespace)}`,
    { method: "POST", body: group },
  );
}

export function deleteRadgroup(identity: CallerIdentity, namespace: string, groupname: string) {
  return callGateway<unknown>(
    identity,
    config().gatewayBase,
    `${RADGROUP_PREFIX}/radgroup/${encodeURIComponent(namespace)}/${encodeURIComponent(groupname)}`,
    { method: "DELETE" },
  );
}

type PolicyKind = "check" | "reply" | "label";

function policyPath(kind: PolicyKind, verb: "add" | "getall", ns: string, group: string) {
  return `${RADPOLICY_PREFIX}/radgroup/${verb}${kind}/${encodeURIComponent(ns)}/${encodeURIComponent(group)}`;
}

export function addPolicyAttribute(
  identity: CallerIdentity,
  kind: PolicyKind,
  attr: RadgroupAttribute,
) {
  return callGateway<RadgroupAttribute>(
    identity,
    config().gatewayBase,
    policyPath(kind, "add", attr.namespace, attr.groupname),
    { method: "POST", body: attr },
  );
}

export function listPolicyAttributes(
  identity: CallerIdentity,
  kind: PolicyKind,
  namespace: string,
  groupname: string,
) {
  return callGateway<RadgroupAttribute[]>(
    identity,
    config().gatewayBase,
    policyPath(kind, "getall", namespace, groupname),
  );
}

export interface PlanDraft {
  groupname: string;
  heading: string;
  description: string;
  check: RadgroupAttribute[];
  reply: RadgroupAttribute[];
  label: RadgroupAttribute[];
}

export interface PlanCreated {
  group: RadGroup;
  attributesWritten: number;
}

/**
 * 🔑 PREFLIGHT BEFORE THE FIRST WRITE, NOT BEST-EFFORT AFTER IT.
 *
 * The group is cheap to create and expensive to have created wrongly: a group
 * with no attributes is a live plan with no limits, and nothing in the estate
 * would flag it -- it looks exactly like a plan whose attributes have not been
 * filled in yet.
 *
 * So radpolicy is proven REACHABLE AND WRITABLE before radcatalog is touched.
 * The probe is a read against the same prefix, same token, same gateway: if
 * that 404s (prefix not published), 401s or 403s, we have not yet written
 * anything and the operator gets one clear failure instead of a half-made plan.
 *
 * This is not paranoia about a hypothetical. As of 2026-08-30 the radpolicy
 * prefix is NOT published on access.ankhnet.io and radaccess has no proxy entry
 * for it at all, so this preflight is what stands between "Create Plan is not
 * available yet" and "Create Plan silently makes unlimited plans".
 */
export async function createPlan(
  identity: CallerIdentity,
  namespace: string,
  draft: PlanDraft,
): Promise<PlanCreated> {
  const attrs = [...draft.check, ...draft.reply, ...draft.label];
  if (attrs.length === 0) {
    throw new ApiError(
      "fail-closed",
      "A plan with no attributes would authorise subscribers with no quota, " +
        "no expiry and no bandwidth ceiling. Add at least one attribute.",
    );
  }

  // PREFLIGHT: does the radpolicy prefix answer at all?
  //
  // 🪤 A 404 MUST NOT BE READ AS "no such group". A 404 from an nginx ingress
  // that has no location for this prefix and a 404 from an application that has
  // no such row are byte-identical — this estate has already been caught by
  // that once. So 404 fails the preflight, deliberately, even though it is
  // sometimes an honest empty answer. The cost of the false negative is a
  // refused create; the cost of the false positive is a plan with no limits.
  //
  // 🔑 What makes the preflight meaningful is that radpolicy has a DIFFERENT,
  // unambiguous answer for "the group has no attributes yet": 204 No Content
  // (app/handler/radgrouppolicy.go:551). So a reachable radpolicy on a
  // brand-new group returns 204, never 404, and the two cases separate cleanly.
  try {
    await listPolicyAttributes(identity, "check", namespace, draft.groupname);
    // 200 or 204 — the service answered. Proceed.
  } catch (e) {
    const detail = e instanceof Error ? e.message : String(e);
    const why =
      e instanceof ApiError && e.kind === "not-found"
        ? "The /radpolicy prefix is not published on the gateway (404). As of " +
          "2026-08-30 radaccess has no proxy entry for radpolicy at all, so this " +
          "is the expected failure until that ships."
        : `radpolicy did not answer: ${detail}`;
    throw new ApiError(
      "fail-closed",
      "The plan's attributes cannot be written, so NOTHING WAS CREATED — a group " +
        "without attributes is a live plan with no quota, no expiry and no " +
        `bandwidth ceiling, which is worse than no plan at all. ${why}`,
    );
  }

  // 🔑 status:false — CREATED INACTIVE, deliberately, because that is what the
  // portal that operators actually use does. create_submit_plan.php:113 sends
  // 'status' => false on every create AND every edit; activation is a separate
  // act. Creating active would have been the obvious guess and would have made
  // a new plan sellable the instant it was saved, before anyone checked its
  // attributes -- and the attributes are written AFTER the group, so there is a
  // window where an active plan has no limits at all.
  const group: RadGroup = {
    namespace,
    groupname: draft.groupname,
    group_hd: draft.heading,
    group_desc: draft.description,
    status: false,
  };
  const created = await createRadgroup(identity, group);

  let written = 0;
  try {
    for (const a of draft.check) {
      await addPolicyAttribute(identity, "check", { ...a, namespace, groupname: draft.groupname });
      written++;
    }
    for (const a of draft.reply) {
      await addPolicyAttribute(identity, "reply", { ...a, namespace, groupname: draft.groupname });
      written++;
    }
    for (const a of draft.label) {
      await addPolicyAttribute(identity, "label", { ...a, namespace, groupname: draft.groupname });
      written++;
    }
  } catch (e) {
    // The preflight passed and a write still failed. Say exactly what exists
    // now -- a half-made plan the operator does not know about is the worst
    // outcome, and guessing at a rollback we cannot prove is the second worst.
    throw new ApiError(
      "unavailable",
      `Plan "${draft.groupname}" WAS CREATED in ${namespace}, but only ${written} of ` +
        `${attrs.length} attributes were written before the failure. It is INCOMPLETE ` +
        `and will not enforce the limits you set. Delete it or finish it before use. ` +
        `Underlying failure: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  return { group: created.data, attributesWritten: written };
}
